import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as Popover from '@radix-ui/react-popover';
import styles from './styles.module.scss';
import { CopyIcon2, EditIcon3, ShareIcon } from 'assets';
import { DetailsEditModal, DecisionBucketModal, InviteCandidatesModal } from 'components';
import type { DetailsFormData } from 'components';
import { ShareJobPostModal } from 'components/modal-variants/share-job-post-modal';
import { useInviteCandidates, useInviteBulkCandidates, useDecisionBucketUpdate, useJobLiveDetailsUpdate, useToast, useGetAssessmentInfo } from 'hooks';
import { copyToClipBoard } from 'helpers';
import { isTalentAudit } from 'helpers/assistantFlow';
import parse from 'html-react-parser';
import { format, parseISO } from 'date-fns';
import { Routes } from 'router';


interface DecisionBucket {
  label: string;
  range: string;
}

interface JobAssessmentData {
  title: string;
  assessmentTitle: string;
  introText: string;
  aboutRole: string;
  responsibilities: string[];
  qualifications: string[];
  aboutAssessment: string;
  headText: string;
  subHeadText: string;
  instructions: string;
  applicationLink: string;
  editableBuckets: boolean;
  decisions: DecisionBucket[];
}

export type { JobAssessmentData, DecisionBucket };

interface JobAssessmentProps {
  assessmentId: string | null;
  initialData: any;
  onSave: (updatedData: any) => void;
}

type RawDecision = {
  label: string;
  range: string; // e.g., "70%-100%"
};

type FormattedDecision = {
  decision: string;
  rangeStart: number;
  rangeEnd: number;
};

export const formatDecisions = (input: RawDecision[]): { decisions: FormattedDecision[] } => {

  return {
    decisions: input?.map(({ label, range }) => {
      const [startStr, endStr] = range.split('-');
      const rangeStart = parseInt(startStr.replace('%', '').trim(), 10);
      const rangeEnd = parseInt(endStr.replace('%', '').trim(), 10);

      return {
        decision: label,
        rangeStart,
        rangeEnd,
      };
    }),
  };
};

/**
 * Formats instruction text by converting newlines to HTML line breaks
 * and ensuring numbered instructions are properly formatted
 */
export const formatInstructions = (instructions: string): string => {
  if (!instructions) return '';

  // First, normalize the text - trim
  let formatted = instructions.trim();

  // Check if already contains HTML tags (like <br>, <p>, <div>, <ol>, <ul>)
  // If so, don't process further as it's already formatted
  const hasHtmlTags = /<(br|p|div|ol|ul|li)[^>]*>/i.test(formatted);
  if (hasHtmlTags) {
    return formatted;
  }

  // Convert \n to <br/> for proper HTML rendering
  // Split by newlines, trim each line, filter empty lines, and join with <br/>
  formatted = formatted
    .split(/\n/)
    .map(line => line.trim())
    .filter(line => line.length > 0)
    .join('<br/>');

  return formatted;
};

const extractBulletList = (text: string): string[] => {
  const lines = text
    .split('\n')
    .map(line => line.replace(/^[\-•*]\s*/, '').trim())
    .filter(line => line.length > 0 && !line.startsWith('{') && !line.startsWith('}'));

  // Some sections open with a lead-in sentence before the bullets ("Participants
  // may be asked questions related to:"). It's prose, not a list item.
  if (lines.length > 1 && lines[0].endsWith(':')) {
    return lines.slice(1);
  }
  return lines;
};

export const parseJobAssessmentData = (uiHints: any): JobAssessmentData => {
  const emailPreview = uiHints.email_preview || '';

  // The talent audit email uses the same structure with different headings
  // ("evaluation"/"participants" instead of "job"/"candidates"), so every section
  // regex below accepts either wording rather than forking the whole parser.
  const isEvaluation = /Your Evaluation is Live!/i.test(emailPreview);

  const title = /Your Job is Live!/i.test(emailPreview)
    ? 'Your Job is Live! 🥳🥳'
    : isEvaluation
      ? 'Your Evaluation is Live! 🥳🥳'
      : 'Job Assessment';
  const assessmentTitleMatch = emailPreview.match(/(?:Assessment|Evaluation) title:\s*([^\n]+)/i);
  const assessmentTitle = assessmentTitleMatch ? assessmentTitleMatch[1].trim() : 'Untitled Assessment';

  const introText = isEvaluation
    ? 'This is what participants will see when they access your evaluation. You can share this link or invite participants directly.'
    : 'This is what candidates will see when they access your assessment. You can share this link or invite candidates directly.';

  const aboutRoleMatch = emailPreview.match(/About (?:the role|this evaluation)\s*\n+([\s\S]*?)(?=\n+Responsibilities:|\n+What we're looking for|\n+What this evaluation covers|\n+About the assessment|\n+About the process|$)/i);
  let aboutRole = aboutRoleMatch ? aboutRoleMatch[1].trim() : '';
  if (aboutRole.startsWith('{')) {
    aboutRole = `We are looking for a ${assessmentTitle} to join our team. You will be responsible for designing intuitive user experiences, collaborating with cross-functional teams, and delivering high-quality solutions that align with business goals.`;
  }

  const respMatch = emailPreview.match(/(?:Responsibilities:|What this evaluation covers)\s*\n+([\s\S]*?)(?=\n+What we're looking for|\n+Qualification|\n+About the assessment|\n+About the process|$)/i);
  const responsibilities = respMatch ? extractBulletList(respMatch[1]) : [];

  const qualMatch = emailPreview.match(/What we're looking for[^\n]*\n+([\s\S]*?)(?=\n+About the assessment|\n+About the process|$)/i);
  const qualifications = qualMatch ? extractBulletList(qualMatch[1]) : [];

  const aboutAssessmentMatch = emailPreview.match(/About the (?:assessment|process)\s*\n+([\s\S]*?)(?=Application Link:|Evaluation link:|Decision Bucket|$)/i);
  const aboutAssessment = aboutAssessmentMatch ? aboutAssessmentMatch[1].trim() : '';

  const instructionsMatch = emailPreview.match(/Instruction\n([\s\S]*?)Application Link:/i);
  const rawInstructions = instructionsMatch ? instructionsMatch[1].trim() : aboutAssessment;
  const formattedInstructions = formatInstructions(rawInstructions);

  const applicationLink = uiHints.application_link || uiHints.evaluation_link || '';
  const decisionBuckets = uiHints.decision_buckets || [];

  return {
    title,
    assessmentTitle,
    introText,
    aboutRole,
    responsibilities,
    qualifications,
    aboutAssessment,
    headText: introText,
    subHeadText: '',
    instructions: formattedInstructions,
    applicationLink,
    editableBuckets: uiHints.editable_buckets !== false,
    decisions: decisionBuckets.map((bucket: any) => ({
      label: bucket.decision,
      range: `${bucket.range_from}%-${bucket.range_to}%`,
    })),
  };
};

const JobAssessment: React.FC<JobAssessmentProps> = ({
  assessmentId,
  initialData,
  onSave,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { openToast } = useToast();
  const [isTitleEditModalOpen, setIsTitleEditModalOpen] = useState(false);
  const [isDecisionEditModalOpen, setIsDecisionEditModalOpen] = useState(false);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isCopyPopoverOpen, setIsCopyPopoverOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [data, setData] = useState<any>(initialData);
  // The email sections carry audit content but their labels are rendered here, so
  // they need the audit wording too ("About this evaluation", not "About the role").
  const audit = isTalentAudit();
  const [initialCandidates, setInitialCandidates] = useState<{ full_name: string; email: string }[]>([]);
  const { assessmentInfo } = useGetAssessmentInfo(assessmentId);
  const deadline = assessmentInfo?.data?.end_date
    ? format(parseISO(assessmentInfo.data.end_date), 'dd MMMM, yyyy')
    : '';
  const autoInviteParam = searchParams.get('autoInvite');
  const copyPopoverCloseTimer = useRef<number | null>(null);

  // Remove autoInvite param without triggering React Router re-render
  const clearAutoInviteParam = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('autoInvite');
    window.history.replaceState({}, '', url);
  };

  const clearCopyPopoverTimer = () => {
    if (copyPopoverCloseTimer.current !== null) {
      window.clearTimeout(copyPopoverCloseTimer.current);
      copyPopoverCloseTimer.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearCopyPopoverTimer();
    };
  }, []);

  const openCopyPopover = () => {
    clearCopyPopoverTimer();
    setIsCopyPopoverOpen(true);
  };

  const closeCopyPopover = () => {
    clearCopyPopoverTimer();
    setIsCopyPopoverOpen(false);
  };

  const scheduleCopyPopoverClose = () => {
    clearCopyPopoverTimer();
    copyPopoverCloseTimer.current = window.setTimeout(() => {
      setIsCopyPopoverOpen(false);
      copyPopoverCloseTimer.current = null;
    }, 200);
  };

  // Auto-open invite modal from chat flow (componentData), localStorage (review-talents), or autoInvite param
  useEffect(() => {
    const CANDIDATES_TO_INVITE_KEY = "cv_analysis_candidates_to_invite";

    // 1. Chat flow: candidates passed via componentData (highest priority)
    if (initialData?.candidatesToInvite?.length > 0) {
      setInitialCandidates(initialData.candidatesToInvite);
      setIsInviteModalOpen(true);
      localStorage.removeItem(CANDIDATES_TO_INVITE_KEY);
      return;
    }

    // 2. Cross-page flow: candidates from localStorage (review-talents page)
    const storedCandidates = localStorage.getItem(CANDIDATES_TO_INVITE_KEY);
    if (storedCandidates) {
      try {
        const candidates = JSON.parse(storedCandidates);
        if (candidates.length > 0) {
          setInitialCandidates(candidates);
          setIsInviteModalOpen(true);
          localStorage.removeItem(CANDIDATES_TO_INVITE_KEY);
          return;
        }
      } catch (e) {
        console.error('Failed to parse stored candidates:', e);
      }
    }

    // 3. Fallback: autoInvite URL param (review-talents without candidate data)
    if (autoInviteParam === 'true') {
      setIsInviteModalOpen(true);
    }
  }, [assessmentId, autoInviteParam, initialData?.candidatesToInvite])

  // Use the decision bucket update hook
  const { mutate: updateDecisionBucket, isPending: isDecisionPending } = useDecisionBucketUpdate();

  // Use the live job details update hook
  const { mutate: updateJobLiveDetails, isPending: isDetailsPending } = useJobLiveDetailsUpdate();

  // Use the invite candidates hooks
  const { mutate: inviteCandidates, isPending: isInvitePending } = useInviteCandidates();
  const { mutate: inviteBulkCandidates, isPending: isBulkInvitePending } = useInviteBulkCandidates();

  const handleDetailsSave = (updated: DetailsFormData) => {
    const nextData = {
      ...data,
      assessmentTitle: updated.assessmentTitle,
      aboutRole: updated.aboutRole,
      responsibilities: updated.responsibilities,
      qualifications: updated.qualifications,
    };

    const applyLocally = () => {
      setData(nextData);
      onSave(nextData);
      setIsTitleEditModalOpen(false);
    };

    if (!assessmentId) {
      applyLocally();
      return;
    }

    updateJobLiveDetails(
      {
        assessment_id: assessmentId,
        title: updated.assessmentTitle,
        about_role: updated.aboutRole,
        responsibilities: updated.responsibilities,
        qualifications: updated.qualifications,
      },
      { onSuccess: applyLocally },
    );
  };

  const handleDecisionSave = (data: { decisions: { decision: string; rangeStart: number; rangeEnd: number }[] }) => {
    const decisionBuckets = data?.decisions.map(d => ({
      decision: d.decision,
      range_from: d.rangeStart,
      range_to: d.rangeEnd,
    }));

    if (assessmentId) {
      updateDecisionBucket(
        {
          assessmentId,
          decisionBuckets,
        },
        {
          onSuccess: (response) => {
            const newDecisions = response.decision_buckets.map((bucket: any) => ({
              label: bucket.decision,
              range: `${bucket.range_from}%-${bucket.range_to}%`,
            }));
            setData(prev => ({ ...prev, decisions: newDecisions }));
            onSave({ ...data, decisions: newDecisions });
            setIsDecisionEditModalOpen(false);
          },
        }
      );
    } else {
      const newDecisions = decisionBuckets.map(bucket => ({
        label: bucket.decision,
        range: `${bucket.range_from}%-${bucket.range_to}%`,
      }));
      setData({ ...data, decisions: newDecisions });
      onSave({ ...data, decisions: newDecisions });
      setIsDecisionEditModalOpen(false);
    }
  };

  const handleInviteSave = (payload: { candidates?: { full_name: string; email: string }[]; file?: File }) => {
    if (!assessmentId) {
      openToast({
        show: true,
        heading: 'Error',
        text: 'Assessment ID is required to invite candidates',
        type: 'error',
        timeOut: 5000,
      });
      setIsInviteModalOpen(false);
      return;
    }

    if (payload.file) {
      // Handle bulk invite via CSV file
      inviteBulkCandidates(
        { assessmentId, file: payload.file },
        {
          onSuccess: () => {
            openToast({
              show: true,
              heading: 'Success',
              text: 'Candidates invited successfully via CSV',
              type: 'success',
              timeOut: 5000,
            });
            setIsInviteModalOpen(false);
            setInitialCandidates([]);
          },
          onError: (error: any) => {
            openToast({
              show: true,
              heading: 'Error',
              text: error?.response?.data?.message || 'Failed to invite candidates via CSV',
              type: 'error',
              timeOut: 5000,
            });
          },
        }
      );
    } else if (payload.candidates) {
      // Handle manual candidate invite
      inviteCandidates(
        { assessmentId, candidates: payload.candidates },
        {
          onSuccess: () => {
            openToast({
              show: true,
              heading: 'Success',
              text: 'Candidates invited successfully',
              type: 'success',
              timeOut: 5000,
            });
            setIsInviteModalOpen(false);
            setInitialCandidates([]);
            clearAutoInviteParam();
          },
          onError: (error: any) => {
            openToast({
              show: true,
              heading: 'Error',
              text: error?.response?.data?.message || 'Failed to invite candidates',
              type: 'error',
              timeOut: 5000,
            });
          },
        }
      );
    }
  };

  const formattedData = formatDecisions(data?.decisions);

  const buildSectionCopyText = (): string => {
    const lines: string[] = [];
    const roleTitle = data?.assessmentTitle || data?.title;
    if (roleTitle) lines.push(`Role: ${roleTitle}`);
    if (data?.aboutRole) lines.push('', 'About the role', data.aboutRole);
    if (data?.responsibilities?.length) {
      lines.push('', 'Responsibilities');
      data.responsibilities.forEach((item: string) => lines.push(`- ${item}`));
    }
    if (data?.applicationLink) lines.push('', `${audit ? 'Evaluation link' : 'Application Link'}: ${data.applicationLink}`);
    if (deadline) lines.push('', `Deadline: ${deadline}`);
    return lines.join('\n');
  };

  const buildShareDraftMessage = (): string => {
    const roleTitle = data?.assessmentTitle ?? data?.title ?? 'this role';
    const applicationLink = data?.applicationLink ?? '';

    return [
      `We are hiring for ${roleTitle}.`,
      applicationLink ? `Apply here: ${applicationLink}` : '',
    ].filter(Boolean).join('\n\n');
  };

  const shareDraftMessage = buildShareDraftMessage();
  const shareLink = data?.applicationLink ?? '';

  const handleCopyApplicationLink = () => {
    if (!data?.applicationLink) return;

    copyToClipBoard({
      link: data.applicationLink,
      onSuccess: () => {
        openToast({
          show: true,
          heading: 'Success',
          text: `${audit ? 'Evaluation' : 'Application'} link copied to clipboard!`,
          type: 'info',
          timeOut: 5000,
        });
        closeCopyPopover();
      },
      onError: () => {
        openToast({
          show: true,
          heading: 'Error',
          text: 'Failed to copy application link.',
          type: 'error',
          timeOut: 5000,
        });
      },
    });
  };

  const handleCopyJobPost = () => {
    copyToClipBoard({
      link: buildSectionCopyText(),
      onSuccess: () => {
        openToast({
          show: true,
          heading: 'Success',
          text: 'Job post copied to clipboard!',
          type: 'info',
          timeOut: 5000,
        });
        closeCopyPopover();
      },
      onError: () => {
        openToast({
          show: true,
          heading: 'Error',
          text: 'Failed to copy job post.',
          type: 'error',
          timeOut: 5000,
        });
      },
    });
  };

  return (
    <div className={styles.jobassessment}>
      <section className={styles.titleinstructionsection}>
        <div className={styles.sectionheadertop}>
          <div className={styles.headerleft}>
            <h2 className={styles.sectiontitle}>{data?.title}</h2>
            <p className={styles.subheading}>
              <span className={styles.subheadingkey}>{audit ? 'Evaluation title: ' : 'Assessment title: '}</span>
              <span className={styles.subheadingvalue}>{data?.assessmentTitle}</span>
            </p>
            <p className={styles.introtext}>{data?.introText}</p>

            <div className={styles.subsection}>
              <h3 className={styles.subsectiontitle}>
                {audit ? 'About this evaluation' : 'About the role'}
              </h3>
              <p className={styles.subsectiontext}>{data?.aboutRole}</p>
            </div>

            {data?.responsibilities?.length > 0 && (
              <div className={styles.subsection}>
                <h3 className={styles.subsectiontitle}>{audit ? 'What this evaluation covers' : 'Responsibilities'}</h3>
                <ul className={styles.bulletlist}>
                  {data.responsibilities.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            {data?.qualifications?.length > 0 && (
              <div className={styles.subsection}>
                <h3 className={styles.subsectiontitle}>
                  What we're looking for/ Qualification
                </h3>
                <ul className={styles.bulletlist}>
                  {data.qualifications.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className={styles.subsection}>
              <h3 className={styles.subsectiontitle}>{audit ? 'About the process' : 'About the assessment'}</h3>
              <div className={styles.subtitle}>
                {data?.instructions?.length > 0
                  ? parse(data.instructions)
                  : data?.aboutAssessment}
              </div>
            </div>
          </div>
          <div className={styles.actionbutton}>
            <EditIcon3 onClick={() => setIsTitleEditModalOpen(true)} />
          </div>
        </div>
        <div className={styles.applicationlink}>
          <p>{audit ? 'Evaluation link:' : 'Application Link:'}</p>
          <div className={styles.applicationlinktext}>
            <a href={data?.applicationLink} target="_blank" rel="noopener noreferrer">
              {data?.applicationLink}
            </a>
          </div>
          <div className='flex gap-2 items-center'>
            <Popover.Root
              open={isCopyPopoverOpen}
              onOpenChange={(open) => {
                if (open) {
                  openCopyPopover();
                } else {
                  closeCopyPopover();
                }
              }}
              modal={false}
            >
              <Popover.Trigger asChild>
                <button
                  type="button"
                  className="flex gap-2 mt-2 items-center bg-transparent border-0 p-0 cursor-pointer"
                  onPointerEnter={openCopyPopover}
                  onPointerLeave={scheduleCopyPopoverClose}
                  aria-label="Copy options"
                >
                  <CopyIcon2 />
                </button>
              </Popover.Trigger>
              <Popover.Portal>
                <Popover.Content
                  forceMount
                  side="bottom"
                  align="start"
                  sideOffset={6}
                  collisionPadding={12}
                  className="z-[1000] w-[200px] rounded-[16px] bg-white p-3 shadow-[0px_12px_30px_rgba(16,24,40,0.18)] border border-[#EAECF0] outline-none transition-[opacity,transform] duration-150 ease-out data-[state=open]:opacity-100 data-[state=open]:translate-y-0 data-[state=open]:scale-100 data-[state=closed]:opacity-0 data-[state=closed]:translate-y-1 data-[state=closed]:scale-95 data-[state=closed]:pointer-events-none"
                  onPointerEnter={openCopyPopover}
                  onPointerLeave={scheduleCopyPopoverClose}
                  onPointerDownOutside={closeCopyPopover}
                >
                  <button
                    type="button"
                    onClick={handleCopyApplicationLink}
                    className="w-full rounded-[12px] px-4 py-3 text-left text-[#344054] text-[16px] hover:bg-[#F9FAFB] transition-colors"
                  >
                    Copy link
                  </button>
                  <button
                    type="button"
                    onClick={handleCopyJobPost}
                    className="w-full rounded-[12px] px-4 py-3 text-left text-[#344054] text-[16px] hover:bg-[#F9FAFB] transition-colors"
                  >
                    Copy job post
                  </button>
                </Popover.Content>
              </Popover.Portal>
            </Popover.Root>
            <button
              type="button"
              onClick={() => setIsShareModalOpen(true)}
              className="mt-1.5"
              aria-label="Open share post modal"
            >
              <ShareIcon />
            </button>
          </div>


        </div>
      </section>

      <section className={styles.decisionsection}>
        <div className={styles.sectionheader}>
          <h2 className={styles.sectiontitle}>Decision Bucket</h2>
          {data?.editableBuckets !== false && (
            <div className={styles.actionbuttons}>
              <EditIcon3 onClick={() => setIsDecisionEditModalOpen(true)} />
            </div>
          )}
        </div>
        <p className={styles.description}>
          I will categorize the {isTalentAudit() ? 'participants' : 'candidates'} into the decision bucket below based on their overall assessment scores.
        </p>
        <ol className={styles.decisionlist}>
          {data?.decisions.map((decision, idx) => (
            <li key={idx} className={styles.decisionitem}>
              {decision.label} ({decision.range})
            </li>
          ))}
        </ol>

      </section>
      <section className={styles.buttons}>
        {/* <button
          className={styles.publishbutton}
          onClick={() =>
            openToast({
              show: true,
              heading: 'Coming soon',
              text: 'Publishing to the job board will be available shortly.',
              type: 'info',
              timeOut: 5000,
            })
          }
        >
          Publish to job board
        </button> */}
        <button
          className={styles.invitebutton}
          onClick={() => setIsInviteModalOpen(true)}
          disabled={isInvitePending || isBulkInvitePending}
        >
          {isTalentAudit() ? 'Invite employees' : 'Invite candidates'}
        </button>
        <button className={styles.viewbutton} onClick={() => navigate(Routes.manageJobs + '?filter=all&page=1')}>
          {isTalentAudit() ? 'View talent dashboard' : 'View your hiring dashboard'}
        </button>
      </section>
      <DetailsEditModal
        isOpen={isTitleEditModalOpen}
        onClose={() => setIsTitleEditModalOpen(false)}
        currentTitle={data?.assessmentTitle}
        currentAboutRole={data?.aboutRole}
        currentResponsibilities={data?.responsibilities}
        currentQualifications={data?.qualifications}
        onSave={handleDetailsSave}
        isLoading={isDetailsPending}
      />
      <DecisionBucketModal
        isOpen={isDecisionEditModalOpen}
        onClose={() => setIsDecisionEditModalOpen(false)}
        onSave={handleDecisionSave}
        isLoading={isDecisionPending}
        currentData={formattedData}
      />
      <InviteCandidatesModal
        isOpen={isInviteModalOpen}
        onClose={() => {
          setIsInviteModalOpen(false);
          clearAutoInviteParam();
        }}
        onSave={handleInviteSave}
        isLoading={isInvitePending || isBulkInvitePending}
        assessmentId={assessmentId}
        initialCandidates={initialCandidates}
      />
      <ShareJobPostModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        shareLink={shareLink}
        shareDraftMessage={shareDraftMessage}
      />
    </div>
  );
};

export default JobAssessment;